import {
	HERO_ASSETS,
	REFERENCE_HEIGHT,
	REFERENCE_WIDTH,
	TEXT_SAFE_AREA,
	type HeroAsset,
	type IconEdge,
} from './layout';

/** Number of points sampled along a segment when checking rect intersection. */
const SEGMENT_SAMPLES = 16;

type Point = { x: number; y: number };

type ReferenceSize = {
	width: number;
	height: number;
};

type TextSafeArea = {
	x: readonly [number, number];
	y: readonly [number, number];
};

const toRefPoint = (asset: HeroAsset, referenceSize: ReferenceSize): Point => ({
	x: asset.x * referenceSize.width,
	y: asset.y * referenceSize.height,
});

const pointInRect = (p: Point, textSafeArea: TextSafeArea) =>
	p.x >= textSafeArea.x[0] &&
	p.x <= textSafeArea.x[1] &&
	p.y >= textSafeArea.y[0] &&
	p.y <= textSafeArea.y[1];

/**
 * True if any point on the segment [a, b] lies inside TEXT_SAFE_AREA.
 * Sampling is sufficient at this scale (~50 nodes, rect with sides 300+px).
 */
const segmentCrossesTextArea = (a: Point, b: Point, textSafeArea: TextSafeArea) => {
	if (pointInRect(a, textSafeArea) || pointInRect(b, textSafeArea)) return true;

	for (let i = 1; i < SEGMENT_SAMPLES; i++) {
		const t = i / SEGMENT_SAMPLES;
		const sample = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
		if (pointInRect(sample, textSafeArea)) return true;
	}

	return false;
};

const distance = (a: Point, b: Point) => {
	const dx = b.x - a.x;
	const dy = b.y - a.y;
	return Math.sqrt(dx * dx + dy * dy);
};

const densityToK = (density: number): number => {
	if (density <= 1) return 2;
	if (density >= 3) return 4;
	return 3;
};

/** Scribbles are decorative — they should float free without any edges. */
const isConnectable = (asset: HeroAsset) => asset.kind !== 'scribble';

/**
 * Build a deterministic edge set by linking each asset to its k nearest
 * neighbours, skipping any segment that crosses the central text area or
 * exceeds `maxEdge`. Pairs are deduped (undirected graph).
 *
 * Density mapping:
 *   1 → k = 2 (sparse)
 *   2 → k = 3 (balanced)
 *   3 → k = 4 (dense)
 *
 * `maxEdge` is a hard cap on edge length in REFERENCE_WIDTH/HEIGHT units.
 * At small radii some nodes may end up with fewer than k edges (or none)
 * if no neighbours are close enough — that's intentional.
 */
export const buildEdgesForAssets = (
	assets: readonly HeroAsset[],
	density: number,
	maxEdge: number,
	textSafeArea: TextSafeArea,
	referenceSize: ReferenceSize,
): IconEdge[] => {
	const k = densityToK(density);
	const points = assets.map((asset) => toRefPoint(asset, referenceSize));
	const seen = new Set<string>();
	const edges: IconEdge[] = [];

	for (let i = 0; i < assets.length; i++) {
		if (!isConnectable(assets[i])) continue;
		const candidates: { index: number; dist: number }[] = [];

		for (let j = 0; j < assets.length; j++) {
			if (i === j) continue;
			if (!isConnectable(assets[j])) continue;
			const dist = distance(points[i], points[j]);
			if (dist > maxEdge) continue;
			if (segmentCrossesTextArea(points[i], points[j], textSafeArea)) continue;
			candidates.push({ index: j, dist });
		}

		candidates.sort((a, b) => a.dist - b.dist);

		for (const { index: j } of candidates.slice(0, k)) {
			const a = assets[i].id;
			const b = assets[j].id;
			const key = a < b ? `${a}__${b}` : `${b}__${a}`;
			if (seen.has(key)) continue;
			seen.add(key);
			edges.push({ from: a, to: b });
		}
	}

	return edges;
};

export const buildEdges = (density: number, maxEdge: number): IconEdge[] =>
	buildEdgesForAssets(HERO_ASSETS, density, maxEdge, TEXT_SAFE_AREA, {
		width: REFERENCE_WIDTH,
		height: REFERENCE_HEIGHT,
	});
